/** @jsx jsx */
import { jsx } from "theme-ui"
import { useDropzone } from "react-dropzone"
import React, { useState, useCallback, useRef } from "react"
import imageCompression from "browser-image-compression"
import Cropper from "react-easy-crop"
import FileDropzone from "../file-dropzone"
import { useLocation } from "@reach/router"
import CTA from "../cta"

const FieldImage = ({ register, field, validate, setValue, watchData }) => {
  const location = useLocation()
  const isOrganizationPage = location.pathname.includes("organization-submission")

  const [imageSrc, setImageSrc] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoomState, setZoomState] = useState(1)
  const zoomRef = useRef(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState(null)

  React.useEffect(() => {
    register(
      { name: field.name },
      { required: !!field.required, ...(validate && { validate }) }
    )
  }, [register, field.name, field.required, validate])

  const createImage = url =>
    new Promise((resolve, reject) => {
      const img = new Image()
      img.addEventListener("load", () => resolve(img))
      img.addEventListener("error", err => reject(err))
      img.src = url
    })

  const getCroppedImg = async (imageSrc, cropPixels) => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    const targetWidth = 1200
    const targetHeight = 675

    canvas.width = targetWidth
    canvas.height = targetHeight

    ctx.drawImage(
      image,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      targetWidth,
      targetHeight
    )

    return new Promise(resolve => {
      canvas.toBlob(async blob => {
        if (!blob) return

        const compressedBlob = await imageCompression(blob, {
          maxSizeMB: 5,
          maxWidthOrHeight: 1200,
          useWebWorker: true,
          fileType: "image/jpeg",
        })

        const compressedFile = new File([compressedBlob], "event-image.jpg", {
          type: "image/jpeg",
        })

        const compressedUrl = URL.createObjectURL(compressedFile)
        resolve({ compressedFile, compressedUrl })
      }, "image/jpeg", 0.8)
    })
  }

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels)
  }, [])

  const generatePreview = async () => {
    try {
      const { compressedFile, compressedUrl } = await getCroppedImg(
        imageSrc,
        croppedAreaPixels
      )
      setPreview(compressedUrl)
      setValue(field.name, [compressedFile], { shouldDirty: true })
      setError(null)
    } catch (err) {
      setError("Something went wrong while cropping.")
      console.error(err)
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    multiple: false,
    maxSize: 5 * 1024 * 1024,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0]
      const reader = new FileReader()
      reader.onload = () => setImageSrc(reader.result)
      reader.readAsDataURL(file)
    },
  })

  if (isOrganizationPage) {
    return (
      <FileDropzone
        multiple={false}
        onDrop={files =>
          setValue(field.name, files, {
            shouldDirty: true,
          })
        }
      >
        {field.children}
      </FileDropzone>
    )
  }

  return (
    <div>
      {!imageSrc && (
        <div
          {...getRootProps()}
          sx={{
            border: "2px dashed gray",
            padding: "30px",
            width: "100%",
            maxWidth: "400px",
            margin: "20px auto",
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <p>Drag and drop your event image here or choose a file</p>
        </div>
      )}

      {imageSrc && (
        <>
          <div sx={{ textAlign: "center", maxWidth: "400px", margin: "0 auto 10px" }}>
            <p>
              Use your touchpad and pinch to zoom. Click and drag to reposition the
              image and bring the section you want into view.
            </p>
          </div>
          <div
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              height: "225px",
              margin: "20px auto",
            }}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoomState}
              onCropChange={setCrop}
              onZoomChange={(val) => {
                zoomRef.current = val
                setZoomState(val)
              }}
              onCropComplete={handleCropComplete}
              aspect={16 / 9}
              cropShape="rect"
              showGrid={false}
            />
          </div>

          <div sx={{ textAlign: "center", marginTop: "10px" }}>
            <button
              type="button"
              onClick={() => {
                const newZoom = Math.max(1, zoomRef.current - 0.1)
                zoomRef.current = newZoom
                setZoomState(newZoom)
              }}
              sx={{
                variant: "buttons.primary",
                mr: 2,
                px: 3,
                py: 1,
                fontSize: 2,
                borderRadius: "6px",
              }}
            >
              −
            </button>
            <button
              type="button"
              onClick={() => {
                const newZoom = Math.min(3, zoomRef.current + 0.1)
                zoomRef.current = newZoom
                setZoomState(newZoom)
              }}
              sx={{
                variant: "buttons.primary",
                px: 3,
                py: 1,
                fontSize: 2,
                borderRadius: "6px",
              }}
            >
              +
            </button>
          </div>

          <div sx={{ textAlign: "center", marginBottom: "20px", marginTop: "10px" }}>
            <CTA onClick={generatePreview}>Crop & Compress</CTA>
            <CTA onClick={() => setImageSrc(null)} sx={{ ml: "10px" }}>
              Start Over
            </CTA>
          </div>
        </>
      )}

      {error && <p sx={{ color: "red", textAlign: "center" }}>{error}</p>}

      {preview && (
        <div sx={{ textAlign: "center", padding: '1rem' }}>
          <h5>Final Preview (1200x675)</h5>
          <img
            src={preview}
            alt="Final"
            sx={{ width: "100%", maxWidth: "600px", borderRadius: "8px" }}
          />
        </div>
      )}
    </div>
  )
}
export default FieldImage
